import React, { useState, useEffect, useRef, useCallback } from 'react';
import "./PrintLabelForm.css";

// Same endpoint InventorySearch.js / InventoryTransferForm.js use
// (server.js: app.get('/api/products', ...)). all=true bypasses pagination
// and returns every matching row instead of just the current 50-item page.
const SEARCH_ENDPOINT = '/api/products';
const SEARCH_DEBOUNCE_MS = 300;

const PrintLabelForm = ({ inventory = [], onPrintLabelComplete, fetchInventory, userId }) => {
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching, use `inventory` prop
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const selectAllRef = useRef(null);

  // Every item we've ever seen (initial page or any search result), keyed by
  // item_code, so a selected item's data is still resolvable at submit time
  // even if it isn't in the currently displayed list anymore.
  const itemsByCodeRef = useRef(new Map());

  const rememberItems = useCallback((items) => {
    items.forEach((item) => {
      if (item?.item_code != null) {
        itemsByCodeRef.current.set(item.item_code, item);
      }
    });
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    rememberItems(inventory);
  }, [inventory, rememberItems]);

  // Debounced server-side search across ALL inventory, not just the current page
  useEffect(() => {
    const trimmed = searchQuery.trim();

    if (!trimmed) {
      setSearchResults(null);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        search: trimmed,
        all: 'true',
      });

      fetch(`${SEARCH_ENDPOINT}?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Search failed (${response.status})`);
          }
          return response.json();
        })
        .then((data) => {
          const items = Array.isArray(data) ? data : (data.items || []);
          rememberItems(items);
          setSearchResults(items);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Error searching inventory:', error);
            setSearchError('Could not search inventory. Showing loaded items only.');
            setSearchResults(null);
          }
        })
        .finally(() => {
          setIsSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery, rememberItems]);

  // Use live search results (across all inventory) when the user has typed
  // something; otherwise fall back to the initially-loaded page of inventory.
  const displayedInventory = searchQuery.trim() ? (searchResults || []) : inventory;

  // Select-all (visible) helpers
  const visibleCodes = displayedInventory.map(i => i.item_code);
  const allVisibleSelected =
    visibleCodes.length > 0 && visibleCodes.every(c => selectedInventory.includes(c));
  const someVisibleSelected =
    visibleCodes.some(c => selectedInventory.includes(c)) && !allVisibleSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const handleInventorySelection = (item_code) => {
    setSelectedInventory((prev) =>
      prev.includes(item_code)
        ? prev.filter(code => code !== item_code)
        : [...prev, item_code]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedInventory(prev => prev.filter(code => !visibleCodes.includes(code)));
    } else {
      setSelectedInventory(prev => Array.from(new Set([...prev, ...visibleCodes])));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedInventory.length === 0) {
      alert('Please select at least one inventory item to print label.');
      return;
    }

    // Resolve selected codes against the accumulated items map so items
    // picked during an earlier search are still included even if they're
    // not in the currently displayed list.
    const itemsToPrint = selectedInventory
      .map((code) => itemsByCodeRef.current.get(code))
      .filter(Boolean);

    // Optional: warn if some items lack EPC
    const withoutEpc = itemsToPrint.filter(i => !i.rfid_epc);
    if (withoutEpc.length) {
      const proceed = window.confirm(
        `${withoutEpc.length} selected item(s) have no RFID EPC. Continue printing anyway?`
      );
      if (!proceed) return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory/printlabel', {
        method: 'PUT', // <-- must be PUT to match your server
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          items: itemsToPrint,
          user_id: userId, // if you want to log who printed
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Print failed (${res.status})`);
      }

      const data = await res.json();
      console.log('Print response:', data);
      alert(`Sent ${data.count ?? itemsToPrint.length} label(s) to printer.`);
      onPrintLabelComplete();
    } catch (err) {
      console.error('Print error:', err);
      alert(`Print error: ${err.message || err}`);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3>Print Label</h3>

      <div>
        <label>Search Inventory Items:</label>
        <input
          type="text"
          placeholder="Search by name, Product code, or serial number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {isSearching && <span className="search-status">Searching…</span>}
        {searchError && <span className="search-status search-status-error">{searchError}</span>}
      </div>

      <div className="inventory-selection-container">
        <table>
          <thead>
            <tr>
              <th>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    ref={selectAllRef}
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Select all visible"
                  />
                </div>
              </th>
              <th>First Name</th>
              <th>Second Name</th>
              <th>Product Code</th>
              <th>Serial Number</th>
            </tr>
          </thead>
          <tbody>
            {displayedInventory.length > 0 ? displayedInventory.map((item) => (
              <tr key={item.item_code}>
                <td>
                  <input
                    type="checkbox"
                    value={item.item_code}
                    checked={selectedInventory.includes(item.item_code)}
                    onChange={() => handleInventorySelection(item.item_code)}
                    aria-label={`Select ${item.item_code}`}
                  />
                </td>
                <td>{item.item_first_name}</td>
                <td>{item.item_second_name}</td>
                <td>{item.item_code}</td>
                <td>{item.serial_number}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="5">
                  {isSearching ? 'Searching…' : 'No inventory items match your search.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedInventory.length > 0 && (
        <div className="selected-count-note">
          {selectedInventory.length} item{selectedInventory.length === 1 ? '' : 's'} selected
        </div>
      )}

      <button type="submit">Print</button>
    </form>
  );
};

export default PrintLabelForm;